// pages/InvoicesPage.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import {
  Box,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Spinner,
  Badge,
  Button,
  useToast,
  Tooltip,
  Stack,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Kbd,
  Flex,
  IconButton,
  useColorModeValue,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  ButtonGroup,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stat,
  StatLabel,
  StatNumber,
  Checkbox,
} from "@chakra-ui/react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { formatDateTime } from "../utils/dateUtils";
import CreateInvoiceModal from "../components/CreateInvoiceModal";
import ViewInvoiceModal from "../components/ViewInvoiceModal";
import { useCognito } from "../utils/useCognito";
import { markInvoiceState } from "../utils/commonApiCalls";
import ConfirmDialog from "../components/ConfirmDialog";
import InvoiceActionsMenu from "../components/InvoiceActionsMenu";
import { SearchIcon, SmallCloseIcon, RepeatIcon } from "@chakra-ui/icons";
import InvoiceBulkActionBar from "../components/InvoiceBulkActionBar";

const GET_INVOICES_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getInvoice";
const GET_ORDERS_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getAllMedicalOrders";
const USERS_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getUser";
const BAA_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getBaaData";

// 🔐 Admin endpoints
const GET_INVOICE_PDF_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getInvoicePdf";
const BULK_INVOICE_ZIP_API =
  "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/bulkInvoiceZip";

const HOVER_SCALE = { transform: "scale(1)" };

const getDueBadgeColor = (dueDate) => {
  if (!dueDate) return "gray";
  const now = new Date();
  const date = new Date(dueDate);
  const diffDays = Math.floor((date - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return "purple";
  if (diffDays <= 7) return "red";
  if (diffDays <= 14) return "orange";
  if (diffDays <= 30) return "yellow";
  return "green";
};

const normalize = (s) => (s || "").toString().toLowerCase().trim();
const safeJoin = (...parts) =>
  parts
    .map((p) => (typeof p === "string" ? p : ""))
    .filter(Boolean)
    .join(" ");

const currency = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const calculateInvoiceStats = (invoices) => {
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  let totalPaidCount = 0;
  let totalOverdueCount = 0;
  let totalUpcomingCount = 0;
  let totalPaidAmount = 0;
  let totalOverdueAmount = 0;
  let totalUpcomingAmount = 0;

  invoices.forEach((invoice) => {
    const amount = Number(invoice.totalDue || 0);

    if (invoice.paid) {
      totalPaidCount++;
      totalPaidAmount += amount;
    } else {
      if (invoice.dueDate) {
        const dueDate = new Date(invoice.dueDate);
        dueDate.setHours(0, 0, 0, 0);
        if (dueDate < now) {
          totalOverdueCount++;
          totalOverdueAmount += amount;
        } else {
          totalUpcomingCount++;
          totalUpcomingAmount += amount;
        }
      } else {
        totalUpcomingCount++;
        totalUpcomingAmount += amount;
      }
    }
  });

  return {
    totalPaidCount,
    totalOverdueCount,
    totalUpcomingCount,
    totalPaidAmount,
    totalOverdueAmount,
    totalUpcomingAmount,
  };
};

const StatisticsBox = ({ invoices }) => {
  const stats = calculateInvoiceStats(invoices);
  return (
    <Box mb={4} p={4} bg="white" borderRadius="lg" borderWidth="1px" borderColor="gray.200">
      <HStack spacing={6} flexWrap="wrap">
        <Stat minW="150px">
          <StatLabel fontSize="sm" color="gray.600">
            Total Paid
          </StatLabel>
          <StatNumber fontSize="xl" color="green.600">
            {stats.totalPaidCount}
          </StatNumber>
          <Text fontSize="sm" color="green.500" fontWeight="medium">
            {currency.format(stats.totalPaidAmount)}
          </Text>
        </Stat>
        <Stat minW="150px">
          <StatLabel fontSize="sm" color="gray.600">
            Overdue
          </StatLabel>
          <StatNumber fontSize="xl" color="red.500">
            {stats.totalOverdueCount}
          </StatNumber>
          <Text fontSize="sm" color="red.400" fontWeight="medium">
            {currency.format(stats.totalOverdueAmount)}
          </Text>
        </Stat>
        <Stat minW="150px">
          <StatLabel fontSize="sm" color="gray.600">
            Upcoming
          </StatLabel>
          <StatNumber fontSize="xl" color="blue.500">
            {stats.totalUpcomingCount}
          </StatNumber>
          <Text fontSize="sm" color="blue.400" fontWeight="medium">
            {currency.format(stats.totalUpcomingAmount)}
          </Text>
        </Stat>
      </HStack>
    </Box>
  );
};

const InvoicesPage = () => {
  const { getIdToken } = useCognito();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const isTestMode = searchParams.get("test") === "true";
  const highlightedInvoiceId = searchParams.get("highlight");
  const initialTabIndex = searchParams.get("tab") === "distributors" ? 1 : 0;

  const [tabIndex, setTabIndex] = useState(initialTabIndex);
  const [loading, setLoading] = useState(true);
  const [groupedInvoices, setGroupedInvoices] = useState({});
  const [openSections, setOpenSections] = useState({});
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [sendingReminderFor, setSendingReminderFor] = useState(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [selectedFacilityName, setSelectedFacilityName] = useState("");
  const [showEditModal, setShowEditModal] = useState(false);
  const [editInitialData, setEditInitialData] = useState(null);
  const [updatingInvoice, setUpdatingInvoice] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    invoice: null,
    newState: null,
  });

  // ✅ Selection state (global across facilities)
  const [selectedInvoices, setSelectedInvoices] = useState(() => new Set());
  const [isBulkDownloading, setIsBulkDownloading] = useState(false);

  // Search + Filter
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | paid | unpaid
  const [viewMode, setViewMode] = useState("patient"); // 'patient' | 'date'
  const searchRef = useRef(null);

  const toast = useToast();
  const headerBg = useColorModeValue("white", "gray.800");
  const panelBg = useColorModeValue("gray.50", "gray.900");

  // Keep tab in URL, but never change pathname (always /invoices)
  const syncTabToUrl = useCallback(
    (index) => {
      const params = new URLSearchParams(window.location.search);
      if (index === 1) params.set("tab", "distributors");
      else params.delete("tab");
      const qs = params.toString();
      navigate({ search: qs ? `?${qs}` : "" }, { replace: true });
    },
    [navigate]
  );

  useEffect(() => {
    syncTabToUrl(tabIndex);
  }, [tabIndex, syncTabToUrl]);

  const toggleSection = (facilityName) => {
    setOpenSections((prev) => ({
      ...prev,
      [facilityName]: !prev[facilityName],
    }));
  };

  const expandAll = () => {
    const next = {};
    Object.keys(groupedInvoices).forEach((k) => (next[k] = true));
    setOpenSections(next);
  };
  const collapseAll = () => setOpenSections({});

  // Focus search with ⌘/Ctrl + K
  useEffect(() => {
    const handler = (e) => {
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      if (
        (isMac && e.metaKey && e.key.toLowerCase() === "k") ||
        (!isMac && e.ctrlKey && e.key.toLowerCase() === "k")
      ) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Debounce query
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  const copyToClipboard = useCallback(
    async (label, value) => {
      try {
        await navigator.clipboard.writeText(value);
        toast({
          title: `${label} copied`,
          description: value,
          status: "success",
          duration: 1800,
          isClosable: true,
        });
      } catch (e) {
        toast({
          title: "Copy failed",
          description: "Your browser blocked clipboard access.",
          status: "error",
          duration: 2500,
          isClosable: true,
        });
      }
    },
    [toast]
  );

  const handleMarkInvoice = (invoice, newState) => {
    setConfirmDialog({ isOpen: true, invoice, newState });
  };

  const applyInvoiceLocalUpdate = (invoiceId, patch) => {
    setGroupedInvoices((prev) => {
      const next = { ...prev };
      for (const facility of Object.keys(next)) {
        next[facility] = next[facility].map((inv) =>
          inv.invoiceId === invoiceId ? { ...inv, ...patch } : inv
        );
      }
      return next;
    });
  };

  const confirmMarkInvoice = async () => {
    const { invoice, newState } = confirmDialog;
    try {
      setUpdatingInvoice(invoice.invoiceId);
      const token = getIdToken();
      await markInvoiceState(token, invoice.invoiceId, newState, invoice.orderId);

      applyInvoiceLocalUpdate(invoice.invoiceId, {
        paid: !!newState,
        updatedAt: new Date().toISOString(),
      });

      toast({
        title: `Invoice marked as ${newState ? "Paid" : "Unpaid"}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      setConfirmDialog({ isOpen: false, invoice: null, newState: null });
    } catch (err) {
      console.error("Failed to update invoice state", err);
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setUpdatingInvoice(null);
    }
  };

  const handleSendReminder = async (invoice) => {
    try {
      setSendingReminderFor(invoice.invoiceId);

      const token = getIdToken();
      const res = await fetch(
        "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/sendEmail",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            to: invoice.userEmail,
            template: "medical_payment_reminder",
            data: {
              orderId: invoice.orderId,
              dueDate: invoice.dueDate,
              invoiceId: invoice.invoiceId,
            },
          }),
        }
      );

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || "Failed to send email.");
      }

      toast({
        title: "Reminder sent!",
        description: `Email sent to ${invoice.userEmail}`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (err) {
      console.error("Failed to send reminder:", err.message);
      toast({
        title: "Error",
        description: err.message,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSendingReminderFor(null);
    }
  };

  // ✅ Toggle single invoice selection
  const handleToggleInvoice = useCallback((invoiceId) => {
    setSelectedInvoices((prev) => {
      const next = new Set(prev);
      if (next.has(invoiceId)) next.delete(invoiceId);
      else next.add(invoiceId);
      return next;
    });
  }, []);

  // ✅ Facility-level select/deselect all
  const handleFacilityToggleAll = useCallback((invoiceIds) => {
    setSelectedInvoices((prev) => {
      const next = new Set(prev);
      const allSelected = invoiceIds.every((id) => next.has(id));

      if (allSelected) {
        invoiceIds.forEach((id) => next.delete(id));
      } else {
        invoiceIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  // ✅ Single "View PDF" action – ensure up-to-date on backend & open pre-signed URL
  const handleViewPdf = useCallback(
    async (invoice, options = {}) => {
      const { regenerate = false } = options || {};

      try {
        const token = getIdToken();
        const res = await fetch(GET_INVOICE_PDF_API, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            invoiceId: invoice.invoiceId,
            test: isTestMode,
            regenerate, // 👈 NEW: force regeneration when true
          }),
        });

        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.message || "Failed to generate invoice PDF.");
        }

        const url = data.url || data.downloadUrl;
        if (!url) {
          throw new Error("PDF endpoint did not return a URL.");
        }

        window.open(url, "_blank");
      } catch (err) {
        console.error("View PDF error:", err);
        toast({
          title: "Unable to open PDF",
          description: err.message,
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    },
    [getIdToken, isTestMode, toast]
  );

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const token = getIdToken();

        const [invoicesRes, ordersRes, usersRes, baaRes] = await Promise.all([
          fetch(GET_INVOICES_API, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ test: isTestMode }),
          }),
          fetch(GET_ORDERS_API, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ test: isTestMode }),
          }),
          fetch(USERS_API, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ test: isTestMode }),
          }),
          fetch(BAA_API, {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ test: isTestMode }),
          }),
        ]);

        const invoiceData = await invoicesRes.json();
        const ordersData = await ordersRes.json();
        const usersData = await usersRes.json();
        const baaData = await baaRes.json();

        const ordersFlat = Object.values(ordersData.ordersByUser || {}).flat();
        const orderMap = Object.fromEntries(ordersFlat.map((o) => [o.orderId, o]));

        const users = usersData.users || [];
        const baas = baaData.baaData || [];
        const userMap = Object.fromEntries(
          users.map((u) => [u.UserId || u.userId, u])
        );

        const baaMap = Object.fromEntries(
          baas.map((b) => {
            let facilityAddress = b.facilityAddress;
            if (typeof facilityAddress === "string") {
              try {
                facilityAddress = JSON.parse(facilityAddress);
              } catch (err) {
                console.error("Failed to parse facilityAddress:", err);
                facilityAddress = {};
              }
            }
            return [b.UserId || b.userId, { ...b, facilityAddress }];
          })
        );

        const enriched = (invoiceData.invoices || []).map((inv) => {
          const user = userMap[inv.userId] || {};
          const baa = baaMap[inv.userId] || {};
          const order = orderMap[inv.orderId] || {};

          const npi =
            baa.facilityAddress?.npi || baa.npi || baa.facility?.npi || "—";

          const patientName =
            order.patientName ||
            safeJoin(order?.patient?.firstName, order?.patient?.lastName) ||
            order?.patient?.name ||
            "—";

          return {
            ...inv,
            userEmail: user.email || "—",
            userName: `${user.FirstName || ""} ${user.LastName || ""}`.trim() || "—",
            facilityName: baa.facilityName || "Unknown Facility",
            facilityNpi: npi,
            ivrId: order.ivrId || "—",
            patientName,
          };
        });

        // Group by facility
        const grouped = {};
        for (const invoice of enriched) {
          const key = invoice.facilityName;
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(invoice);
        }

        setGroupedInvoices(grouped);

        if (highlightedInvoiceId) {
          for (const [facility, list] of Object.entries(grouped)) {
            if (list.some((i) => i.invoiceId === highlightedInvoiceId)) {
              setOpenSections((prev) => ({ ...prev, [facility]: true }));
              break;
            }
          }
          setTimeout(() => {
            const el = document.querySelector(
              `[data-invoice-row="${highlightedInvoiceId}"]`
            );
            if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 0);
        }
      } catch (err) {
        console.error("Failed to fetch invoices", err);
        toast({
          title: "Error loading invoices",
          description: err.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    };

    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getIdToken, isTestMode, toast, highlightedInvoiceId]);

  // Filter across facilities
  const filteredGrouped = useMemo(() => {
    const q = normalize(debouncedQuery);
    const result = {};
    const match = (inv) => {
      const byStatus =
        statusFilter === "all" ||
        (statusFilter === "paid" && inv.paid) ||
        (statusFilter === "unpaid" && !inv.paid);

      if (!byStatus) return false;
      if (!q) return true;

      const haystack = [inv.invoiceId, inv.orderId, inv.ivrId, inv.patientName]
        .map(normalize)
        .join(" ");

      return haystack.includes(q);
    };

    for (const [facility, list] of Object.entries(groupedInvoices)) {
      const keep = list.filter(match);
      if (keep.length) result[facility] = keep;
    }
    return result;
  }, [groupedInvoices, debouncedQuery, statusFilter]);

  // Totals
  const {
    totalCount,
    unpaidCount,
    unpaidTotalFormatted,
    paidCount,
    paidTotalFormatted,
  } = useMemo(() => {
    let total = 0;
    let uCount = 0;
    let uTotal = 0;
    let pCount = 0;
    let pTotal = 0;
    for (const list of Object.values(filteredGrouped)) {
      for (const inv of list) {
        total += 1;
        const amt = Number(inv.totalDue || 0);
        if (!Number.isNaN(amt)) {
          if (!inv.paid) {
            uCount += 1;
            uTotal += amt;
          } else {
            pCount += 1;
            pTotal += amt;
          }
        }
      }
    }
    return {
      totalCount: total,
      unpaidCount: uCount,
      unpaidTotalFormatted: currency.format(uTotal),
      paidCount: pCount,
      paidTotalFormatted: currency.format(pTotal),
    };
  }, [filteredGrouped]);

  const byCreatedDesc = (a, b) =>
    new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

  const SHORT_IVR_LEN = 10;
  const displayIvr = (val) =>
    !val ? "" : val.startsWith("IVR-") ? val : `${val.slice(0, SHORT_IVR_LEN)}…`;

  const IdChip = ({ kind, value }) => {
    if (!value || value === "—") return null;

    const label = kind === "invoice" ? "Invoice" : kind === "order" ? "Order" : "IVR";
    const color = kind === "invoice" ? "gray.800" : "gray.500";
    const fontWeight = kind === "invoice" ? "bold" : "normal";
    const displayValue = kind === "ivr" ? displayIvr(value) : value;

    return (
      <Tooltip label={`Click to copy ${label}: ${value}`} hasArrow>
        <Text
          as="button"
          onClick={(e) => {
            e.stopPropagation();
            copyToClipboard(label, value);
          }}
          fontFamily="mono"
          fontSize="sm"
          color={color}
          fontWeight={fontWeight}
          textAlign="left"
          title={`Copy ${label}`}
          sx={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            wordBreak: "keep-all",
          }}
        >
          {label}: <span>{displayValue}</span>
        </Text>
      </Tooltip>
    );
  };

  const InvoicesTable = ({ invoices, selectedInvoices, onToggleInvoice }) => (
    <Box overflowX="auto">
      <Table variant="striped" size="sm" sx={{ tableLayout: "fixed", width: "100%" }}>
      <Thead position="sticky" top="0" zIndex={1} bg={headerBg} boxShadow="sm">
        <Tr>
          <Th w="32px" />
          <Th w={{ base: "30%", md: "28%", lg: "26%" }}>Invoice / Linked IDs</Th>
          <Th w={{ base: "22%", md: "20%", lg: "18%" }}>User / Patient</Th>
          <Th w={{ base: "9%", md: "8%", lg: "8%" }}>Amount</Th>
          <Th w={{ base: "9%", md: "8%", lg: "8%" }}>Status</Th>
          <Th
            w={{ base: "0%", md: "9%", lg: "9%" }}
            display={{ base: "none", md: "table-cell" }}
          >
            Created
          </Th>
          <Th
            w={{ base: "0%", lg: "9%" }}
            display={{ base: "none", lg: "table-cell" }}
          >
            Updated
          </Th>
          <Th w={{ base: "12%", md: "10%", lg: "12%" }}>Due</Th>
          <Th w={{ base: "10%", md: "8%", lg: "10%" }} textAlign="right">
            Actions
          </Th>
        </Tr>
      </Thead>
      <Tbody>
        {invoices.map((inv) => (
          <Tr
            key={inv.invoiceId}
            data-invoice-row={inv.invoiceId}
            bg={inv.invoiceId === highlightedInvoiceId ? "yellow.100" : undefined}
          >
            <Td>
              <Checkbox
                isChecked={selectedInvoices.has(inv.invoiceId)}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleInvoice(inv.invoiceId);
                }}
                size="sm"
                colorScheme="teal"
                borderColor="var(--primary-color)"
                _hover={{ borderColor: "var(--primary-color)" }}
                _checked={{ 
                  bg: "var(--primary-color)",
                  borderColor: "var(--primary-color)",
                  _hover: { bg: "var(--primary-color)", borderColor: "var(--primary-color)" }
                }}
              />
            </Td>

            <Td>
              <Stack spacing={0.5} fontSize="sm" minW={0}>
                <IdChip kind="invoice" value={inv.invoiceId} />
                {inv.orderId && <IdChip kind="order" value={inv.orderId} />}
                {inv.ivrId && inv.ivrId !== "—" && <IdChip kind="ivr" value={inv.ivrId} />}
              </Stack>
            </Td>

            <Td>
              <Stack spacing={0} fontSize="sm" minW={0}>
                <Tooltip label={inv.userEmail}>
                  <Text fontWeight="medium" noOfLines={1} title={inv.userEmail}>
                    {inv.userName}
                  </Text>
                </Tooltip>
                <Text
                  color="gray.600"
                  noOfLines={1}
                  title={inv.userEmail}
                  sx={{ whiteSpace: "nowrap" }}
                >
                  {inv.userEmail}
                </Text>
                {inv.patientName && inv.patientName !== "—" && (
                  <Text
                    color="gray.500"
                    fontStyle="italic"
                    noOfLines={1}
                    title={inv.patientName}
                  >
                    Patient: {inv.patientName}
                  </Text>
                )}
              </Stack>
            </Td>

            <Td whiteSpace="nowrap">
              {currency.format(Number(inv.totalDue || 0))}
            </Td>

            <Td>
              <Badge colorScheme={inv.paid ? "green" : "red"}>
                {inv.paid ? "Paid" : "Unpaid"}
              </Badge>
            </Td>

            <Td whiteSpace="nowrap" display={{ base: "none", md: "table-cell" }}>
              {formatDateTime(inv.createdAt)}
            </Td>
            <Td whiteSpace="nowrap" display={{ base: "none", lg: "table-cell" }}>
              {formatDateTime(inv.updatedAt)}
            </Td>

            <Td whiteSpace="nowrap">
              <Badge colorScheme={getDueBadgeColor(inv.dueDate)}>
                {inv.dueDate}
              </Badge>
            </Td>

            <Td textAlign="right">
              <InvoiceActionsMenu
                invoice={inv}
                sendingReminderFor={sendingReminderFor}
                updatingInvoice={updatingInvoice}
                onViewPdf={handleViewPdf}
                onSendReminder={(invoice) => handleSendReminder(invoice)}
                onOpenViewModal={(invoice) => {
                  setSelectedInvoiceId(invoice.invoiceId);
                  setSelectedFacilityName(invoice.facilityName);
                }}
                onMarkPaid={(invoice) => handleMarkInvoice(invoice, true)}
                onMarkUnpaid={(invoice) => handleMarkInvoice(invoice, false)}
                onEdit={(invoice) => {
                  setEditInitialData({ invoiceId: invoice.invoiceId, invoice });
                  setShowEditModal(true);
                }}
              />
            </Td>
          </Tr>
        ))}
      </Tbody>
      </Table>
    </Box>
  );

  // ✅ Bulk download handler – ensures PDFs are up-to-date and returns ZIP URL
  const handleBulkDownload = useCallback(async () => {
    const ids = Array.from(selectedInvoices);
    if (!ids.length || isBulkDownloading) return;

    try {
      setIsBulkDownloading(true);
      const token = getIdToken();

      const res = await fetch(BULK_INVOICE_ZIP_API, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          invoiceIds: ids,
          test: isTestMode,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.message || "Failed to generate ZIP for invoices.");
      }

      const url = data.url || data.downloadUrl;
      if (!url) {
        throw new Error("Bulk export did not return a download URL.");
      }

      toast({
        title: "Bulk download ready",
        description: `Downloading ${ids.length} invoice${
          ids.length > 1 ? "s" : ""
        } as ZIP.`,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      window.open(url, "_blank");
    } catch (err) {
      console.error("Bulk download error:", err);
      toast({
        title: "Bulk download failed",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsBulkDownloading(false);
    }
  }, [selectedInvoices, getIdToken, isBulkDownloading, isTestMode, toast]);

  if (loading) {
    return (
      <Box p={8} textAlign="center">
        <Spinner size="xl" />
      </Box>
    );
  }

  const selectedCount = selectedInvoices.size;

  return (
    <>
      <Tabs index={tabIndex} onChange={setTabIndex} variant="enclosed" isFitted>
        <TabList>
          <Tab _hover={HOVER_SCALE}>Medical Providers</Tab>
          <Tab _hover={HOVER_SCALE}>Distributors</Tab>
        </TabList>

        <TabPanels>
          {/* MEDICAL PROVIDERS TAB */}
          <TabPanel p={0}>
            <Box p={[4, 6, 8]}>
              {/* Top bar */}
              <Flex
                direction={{ base: "column", md: "row" }}
                justify="space-between"
                align={{ base: "stretch", md: "center" }}
                gap={3}
                mb={4}
              >
                <Heading size="lg">
                  Admin Invoice Management {isTestMode && "(Test Mode)"}
                </Heading>

                <HStack spacing={2}>
                  <Button
                    variant="outline"
                    onClick={expandAll}
                    _hover={HOVER_SCALE}
                  >
                    Expand All
                  </Button>
                  <Button
                    variant="outline"
                    onClick={collapseAll}
                    _hover={HOVER_SCALE}
                  >
                    Collapse All
                  </Button>
                  <Button
                    colorScheme="teal"
                    onClick={() => setShowCreateModal(true)}
                    _hover={HOVER_SCALE}
                  >
                    Create New Invoice
                  </Button>
                </HStack>
              </Flex>

              {/* Controls */}
              <Flex
                direction={{ base: "column", md: "row" }}
                gap={3}
                align={{ base: "stretch", md: "center" }}
                mb={4}
              >
                <InputGroup maxW={{ base: "100%", md: "520px" }}>
                  <InputLeftElement pointerEvents="none">
                    <SearchIcon color="gray.400" />
                  </InputLeftElement>
                  <Input
                    ref={searchRef}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search patient, invoice ID, order ID, or IVR ID"
                    variant="filled"
                    _focus={{ bg: "white" }}
                  />
                  {query && (
                    <InputRightElement>
                      <IconButton
                        size="sm"
                        aria-label="Clear search"
                        icon={<SmallCloseIcon />}
                        onClick={() => setQuery("")}
                        variant="ghost"
                        _hover={HOVER_SCALE}
                      />
                    </InputRightElement>
                  )}
                </InputGroup>

                <HStack spacing={3} flexWrap="wrap">
                  <HStack spacing={1} fontSize="sm" color="gray.600">
                    <Kbd>
                      {navigator.platform.toUpperCase().includes("MAC")
                        ? "⌘"
                        : "Ctrl"}
                    </Kbd>
                    <Text>+</Text>
                    <Kbd>K</Kbd>
                    <Text>to focus search</Text>
                  </HStack>

                  {/* View mode toggle */}
                  <ButtonGroup isAttached variant="outline" size="sm">
                    <Button
                      onClick={() => setViewMode("date")}
                      isActive={viewMode === "date"}
                      _hover={HOVER_SCALE}
                    >
                      By Date
                    </Button>
                    <Button
                      onClick={() => setViewMode("patient")}
                      isActive={viewMode === "patient"}
                      _hover={HOVER_SCALE}
                    >
                      By Patient
                    </Button>
                  </ButtonGroup>

                  {/* Status filter */}
                  <ButtonGroup isAttached variant="outline" size="sm">
                    <Button
                      onClick={() => setStatusFilter("all")}
                      isActive={statusFilter === "all"}
                      _hover={HOVER_SCALE}
                    >
                      All
                    </Button>
                    <Button
                      onClick={() => setStatusFilter("unpaid")}
                      isActive={statusFilter === "unpaid"}
                      _hover={HOVER_SCALE}
                    >
                      Unpaid
                    </Button>
                    <Button
                      onClick={() => setStatusFilter("paid")}
                      isActive={statusFilter === "paid"}
                      _hover={HOVER_SCALE}
                    >
                      Paid
                    </Button>
                  </ButtonGroup>
                </HStack>
              </Flex>

              {/* Totals ribbon */}
              <Box mb={4} p={3} borderWidth="1px" borderRadius="lg" bg={panelBg}>
                <HStack spacing={4} wrap="wrap">
                  <Badge colorScheme="blue">{totalCount} total</Badge>
                  <Badge colorScheme="red">{unpaidCount} unpaid</Badge>
                  <Badge colorScheme="purple">
                    Unpaid Total: {unpaidTotalFormatted}
                  </Badge>
                  <Badge colorScheme="green">{paidCount} paid</Badge>
                  <Badge colorScheme="teal">
                    Paid Total: {paidTotalFormatted}
                  </Badge>
                </HStack>
              </Box>

              {Object.keys(filteredGrouped).length === 0 ? (
                <Box p={8} textAlign="center" color="gray.500">
                  <RepeatIcon mr={2} />
                  No invoices match your filters.
                </Box>
              ) : null}

              {Object.entries(filteredGrouped).map(([facility, invoicesRaw]) => {
                const isOpen = !!openSections[facility];
                const npi = invoicesRaw[0]?.facilityNpi || "—";

                const invoicesSorted = [...invoicesRaw].sort(byCreatedDesc);

                const byPatient = invoicesSorted.reduce((acc, inv) => {
                  const key =
                    inv.patientName && inv.patientName !== "—"
                      ? inv.patientName
                      : "Unknown Patient";
                  if (!acc[key]) acc[key] = [];
                  acc[key].push(inv);
                  return acc;
                }, {});

                const facilityInvoiceIds = invoicesSorted.map(
                  (inv) => inv.invoiceId
                );
                const facilityAllSelected = facilityInvoiceIds.every((id) =>
                  selectedInvoices.has(id)
                );
                const facilitySomeSelected =
                  !facilityAllSelected &&
                  facilityInvoiceIds.some((id) => selectedInvoices.has(id));

                return (
                  <Box
                    key={facility}
                    mb={6}
                    borderWidth="1px"
                    borderRadius="xl"
                    overflow="hidden"
                    boxShadow="md"
                  >
                    <Box
                      p={4}
                      bg="gray.100"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      cursor="pointer"
                      _hover={{ bg: "gray.200" }}
                      onClick={() => toggleSection(facility)}
                    >
                      <HStack spacing={3} minW={0}>
                        <Checkbox
                          isChecked={facilityAllSelected}
                          isIndeterminate={facilitySomeSelected}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleFacilityToggleAll(facilityInvoiceIds);
                          }}
                          size="sm"
                          colorScheme="teal"
                          borderColor="var(--primary-color)"
                          _hover={{ borderColor: "var(--primary-color)" }}
                          _checked={{ 
                            bg: "var(--primary-color)",
                            borderColor: "var(--primary-color)",
                            _hover: { bg: "var(--primary-color)", borderColor: "var(--primary-color)" }
                          }}
                          _indeterminate={{
                            bg: "var(--primary-color)",
                            borderColor: "var(--primary-color)",
                            _hover: { bg: "var(--primary-color)", borderColor: "var(--primary-color)" }
                          }}
                        />
                        <Box minW={0}>
                          <Heading size="sm" noOfLines={1}>
                            {facility}
                          </Heading>
                          <Text fontSize="sm" color="gray.600" noOfLines={1}>
                            NPI: {npi} • {invoicesSorted.length} invoice
                            {invoicesSorted.length > 1 ? "s" : ""}
                          </Text>
                        </Box>
                      </HStack>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleSection(facility);
                        }}
                        _hover={HOVER_SCALE}
                      >
                        {isOpen ? "Collapse" : "Expand"}
                      </Button>
                    </Box>

                    {isOpen && (
                      <Box p={0}>
                        <Box p={4} pb={0}>
                          <StatisticsBox invoices={invoicesSorted} />
                        </Box>
                        {viewMode === "date" ? (
                          <Box p={4} pt={0}>
                            <InvoicesTable
                              invoices={invoicesSorted}
                              selectedInvoices={selectedInvoices}
                              onToggleInvoice={handleToggleInvoice}
                            />
                          </Box>
                        ) : (
                          <Box p={4} pt={0}>
                            <Box maxH="520px" overflowY="auto">
                              <Accordion allowMultiple defaultIndex={[]}>
                                {Object.entries(byPatient)
                                  .sort(([a], [b]) => a.localeCompare(b))
                                  .map(([patient, list]) => (
                                    <AccordionItem
                                      key={patient}
                                      border="0"
                                      borderTopWidth="1px"
                                    >
                                      <h2>
                                        <AccordionButton _hover={{ bg: "gray.50" }}>
                                          <Box
                                            as="span"
                                            flex="1"
                                            textAlign="left"
                                            py={1}
                                            pr={3}
                                            minW={0}
                                          >
                                            <Text
                                              as="span"
                                              fontWeight="semibold"
                                              noOfLines={1}
                                            >
                                              Patient: {patient}
                                            </Text>
                                            <Text
                                              as="span"
                                              ml={2}
                                              color="gray.500"
                                              fontSize="sm"
                                            >
                                              • {list.length} invoice
                                              {list.length > 1 ? "s" : ""}
                                            </Text>
                                          </Box>
                                          <AccordionIcon />
                                        </AccordionButton>
                                      </h2>
                                      <AccordionPanel pb={4} pt={2}>
                                        <InvoicesTable
                                          invoices={[...list].sort(byCreatedDesc)}
                                          selectedInvoices={selectedInvoices}
                                          onToggleInvoice={handleToggleInvoice}
                                        />
                                      </AccordionPanel>
                                    </AccordionItem>
                                  ))}
                              </Accordion>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}

              {/* CREATE modal */}
              <CreateInvoiceModal
                isOpen={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                mode="create"
                initialData={{}}
                onSuccess={({ invoiceId }) => {
                  navigate(
                    {
                      search: `?highlight=${invoiceId}${
                        isTestMode ? "&test=true" : ""
                      }`,
                    },
                    { replace: true }
                  );
                  setShowCreateModal(false);
                }}
              />

              {/* EDIT modal */}
              <CreateInvoiceModal
                isOpen={showEditModal}
                onClose={() => setShowEditModal(false)}
                mode="edit"
                initialData={editInitialData || {}}
                onSuccess={({ invoiceId }) => {
                  navigate(
                    {
                      search: `?highlight=${invoiceId}${
                        isTestMode ? "&test=true" : ""
                      }`,
                    },
                    { replace: true }
                  );
                  setShowEditModal(false);
                }}
              />

              {/* VIEW modal */}
              <ViewInvoiceModal
                isOpen={!!selectedInvoiceId}
                onClose={() => setSelectedInvoiceId(null)}
                invoiceId={selectedInvoiceId}
                facilityName={selectedFacilityName}
              />

              {/* Confirm dialog */}
              <ConfirmDialog
                isOpen={confirmDialog.isOpen}
                onClose={() =>
                  setConfirmDialog({
                    isOpen: false,
                    invoice: null,
                    newState: null,
                  })
                }
                onConfirm={confirmMarkInvoice}
                title={`Mark invoice as ${
                  confirmDialog.newState ? "Paid" : "Unpaid"
                }?`}
                description={`This will update the invoice status in the system.`}
                confirmText={`Yes, mark ${
                  confirmDialog.newState ? "Paid" : "Unpaid"
                }`}
                isLoading={updatingInvoice === confirmDialog.invoice?.invoiceId}
              />
            </Box>
          </TabPanel>

          {/* DISTRIBUTORS TAB (placeholder) */}
          <TabPanel p={0}>
            <Box p={[4, 6, 8]}>
              <Flex
                direction={{ base: "column", md: "row" }}
                justify="space-between"
                align={{ base: "stretch", md: "center" }}
                gap={3}
                mb={4}
              >
                <Heading size="lg">Distributor Invoices</Heading>
                <HStack spacing={2}>
                  <Button
                    colorScheme="teal"
                    onClick={() => {
                      toast({
                        title: "Coming soon",
                        description:
                          "Distributor invoice creation will live here.",
                        status: "info",
                      });
                    }}
                    _hover={HOVER_SCALE}
                  >
                    Create Distributor Invoice
                  </Button>
                </HStack>
              </Flex>

              <Box borderWidth="1px" borderRadius="lg" p={6} bg={panelBg}>
                <Text color="gray.600">
                  This view will list distributor accounts and their invoices once
                  the new Backend code is wired up.
                </Text>
                <Text mt={2} color="gray.500" fontSize="sm">
                  For now, use <strong>Create New Invoice</strong> on the Medical
                  Providers tab.
                </Text>
              </Box>
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* ✅ Sticky bulk action bar */}
      <InvoiceBulkActionBar
        selectedCount={selectedCount}
        onClear={() => setSelectedInvoices(new Set())}
        onDownload={handleBulkDownload}
        isDownloading={isBulkDownloading}
      />
    </>
  );
};

export default InvoicesPage;
